import { API_BASE_URL, ApiResponse } from '../config/supabase';
import {
  GenerateUserOTPRequest,
  GenerateUserOTPResponse,
  UserLoginRequest,
  UserLoginResponse,
  UserOTPStatus,
  UserProfileResponse,
  UserExistenceResponse
} from '../types/userAuth';

class UserAuthService {
  private baseUrl = `${API_BASE_URL}/user-auth`;

  // Generic error handler
  private handleError(error: any, operation: string): any {
    console.error(`❌ User Auth API error in ${operation}:`, error);
    return {
      success: false,
      error: error.message || 'An error occurred',
      details: error
    };
  }

  // Generic success response
  private handleSuccess<T>(data: T, operation: string): any {
    console.log(`✅ User Auth API ${operation} successful`);
    return {
      success: true,
      data: data
    };
  }

  // ==================== AUTHENTICATION ====================

  // Generate OTP for user login
  async generateOTP(request: GenerateUserOTPRequest): Promise<GenerateUserOTPResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/generate-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });
      
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to generate OTP');
      }
      
      // Return the result directly to preserve the OTP field
      return {
        success: true,
        message: result.message || 'OTP sent successfully',
        otp: result.data?.otp // Include OTP for testing purposes (from data object)
      };
    } catch (error) {
      return this.handleError(error, 'generate OTP');
    }
  }

  // Generate OTP for donation (no user existence check required)
  async generateDonationOTP(mobileNumber: string): Promise<GenerateUserOTPResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/generate-donation-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mobile_number: mobileNumber }),
      });
      
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to generate OTP');
      }
      
      // Return the result directly to preserve the OTP field
      return {
        success: true,
        message: result.message || 'OTP sent successfully',
        otp: result.data?.otp // Include OTP for testing purposes (from data object)
      };
    } catch (error) {
      return this.handleError(error, 'generate donation OTP');
    }
  }

  // User login with OTP
  async userLogin(request: UserLoginRequest): Promise<UserLoginResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });
      
      const result: any = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Login failed');
      }
      
      // Return the result directly since backend already has the correct structure
      return {
        success: true,
        user: result.user,
        token: result.token,
        expiresAt: result.expiresAt,
        expiresIn: result.expiresIn,
        message: result.message
      };
    } catch (error) {
      return this.handleError(error, 'user login');
    }
  }

  // Verify OTP for donation (no user existence check required)
  async verifyDonationOTP(mobileNumber: string, otp: string): Promise<{ 
    success: boolean; 
    message?: string; 
    error?: string;
    data?: {
      rates?: {
        gold_buy_rate: number;
        gold_sell_rate: number;
        silver_buy_rate: number;
        silver_sell_rate: number;
        block_id: string;
        expires_at: string;
      };
    };
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/verify-donation-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mobile_number: mobileNumber, otp: otp }),
      });
      
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'OTP verification failed');
      }
      
      return {
        success: true,
        message: result.message || 'OTP verified successfully',
        data: result.data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'OTP verification failed'
      };
    }
  }

  // Get OTP status
  async getOTPStatus(mobileNumber: string): Promise<{ success: boolean; data?: UserOTPStatus; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/otp-status/${mobileNumber}`);
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get OTP status');
      }
      
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An error occurred'
      };
    }
  }

  // ==================== USER PROFILE ====================

  // Get user profile
  async getUserProfile(mobileNumber: string): Promise<UserProfileResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/profile/${mobileNumber}`);
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get user profile');
      }
      
      return this.handleSuccess(result.data, 'get user profile');
    } catch (error) {
      return this.handleError(error, 'get user profile');
    }
  }

  // ==================== USER EXISTENCE CHECK ====================

  // Check if user exists
  async checkUserExists(mobileNumber: string): Promise<UserExistenceResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/check-user/${mobileNumber}`);
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to check user existence');
      }
      
      return this.handleSuccess(result.data, 'check user existence');
    } catch (error) {
      return this.handleError(error, 'check user existence');
    }
  }

  // ==================== UTILITY METHODS ====================

  // Format mobile number for display
  formatMobileNumber(mobileNumber: string): string {
    if (!mobileNumber) return '';
    
    // Remove any non-digit characters
    const cleaned = mobileNumber.replace(/\D/g, '');
    
    // Format as XXXXX XXXXX (10 digits)
    if (cleaned.length === 10) {
      return `${cleaned.slice(0, 5)} ${cleaned.slice(5, 10)}`;
    }
    
    // Format as XXXXX XXXXX (12 digits, remove country code)
    if (cleaned.length === 12 && cleaned.startsWith('91')) {
      const withoutCountryCode = cleaned.slice(2);
      return `${withoutCountryCode.slice(0, 5)} ${withoutCountryCode.slice(5, 10)}`;
    }
    
    return mobileNumber;
  }

  // Validate mobile number format
  validateMobileNumber(mobileNumber: string): boolean {
    if (!mobileNumber) return false;
    
    // Remove any non-digit characters
    const cleaned = mobileNumber.replace(/\D/g, '');
    
    // Check if it's a valid Indian mobile number
    // Should be 10 digits starting with 6-9, or 12 digits starting with 91
    if (cleaned.length === 10) {
      return /^[6-9]\d{9}$/.test(cleaned);
    }
    
    if (cleaned.length === 12) {
      return /^91[6-9]\d{9}$/.test(cleaned);
    }
    
    return false;
  }

  // Get status color
  getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'active':
      case 'success':
        return '#10b981'; // green
      case 'inactive':
      case 'failed':
      case 'error':
        return '#ef4444'; // red
      case 'pending':
        return '#f59e0b'; // yellow
      default:
        return '#6b7280'; // gray
    }
  }

  // Get status icon
  getStatusIcon(status: string): string {
    switch (status.toLowerCase()) {
      case 'active':
      case 'success':
        return '✅';
      case 'inactive':
      case 'failed':
      case 'error':
        return '❌';
      case 'pending':
        return '⏳';
      default:
        return '❓';
    }
  }
}

export const userAuthService = new UserAuthService(); 